import SealdSDKPluginSSKS2MR from "@seald-io/sdk-plugin-ssks-2mr";
import SealdSDKPluginSSKSPassword from "@seald-io/sdk-plugin-ssks-password";
import SealdSDK, {
  createLocalStorageEncryptionSessionCache,
} from "@seald-io/sdk/browser/seald-sdk.polyfilled.js";
import { SDKArguments, SealdSDK as SealdSDKType } from "@seald-io/sdk/lib/main";
import type { EncryptionSessionCache } from "@seald-io/sdk/lib/main";
import { TRACK_EVENTS } from "core/consts";
import { getError } from "core/model/utils/errors";
import { EnvContext, TrackEventFn } from "core/types";
import { DAY_IN_MS } from "../consts/time";
import { createIndexedDBEncryptionSessionCache } from "./indexedDbCache";
import { isWeb } from "./utils";

export const SEALD_TEST_URL_API = "https://api.seald.io/is_alive/";
export const SEALD_TEST_URL_SSKS = "https://ssks.seald.io/is_alive/";

export let sealdSDKInstance: SealdSDKType | undefined;

const SEALD_CACHE_PREFIX = "seald-cache-";
const SEALD_CACHE_TTL_MS = DAY_IN_MS * 20;

type SealdCacheDiagnostics = {
  entries: number;
  largestEntryBytes: number;
  newestSerializationDate: string | null;
  oldestSerializationDate: string | null;
  totalBytes: number;
};

function getStringBytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

function formatSerializationDate(timestamp: number | null): string | null {
  if (timestamp == null) return null;
  return new Date(timestamp).toISOString();
}

export function getSealdCacheDiagnostics(
  storage: Pick<Storage, "getItem" | "key" | "length">,
): SealdCacheDiagnostics {
  let entries = 0;
  let largestEntryBytes = 0;
  let newestSerializationDate: number | null = null;
  let oldestSerializationDate: number | null = null;
  let totalBytes = 0;

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith(SEALD_CACHE_PREFIX)) continue;

    const value = storage.getItem(key);
    if (value == null) continue;

    entries++;
    const entryBytes = getStringBytes(key) + getStringBytes(value);
    totalBytes += entryBytes;

    if (entryBytes >= largestEntryBytes) {
      largestEntryBytes = entryBytes;
    }

    try {
      const parsed = JSON.parse(value) as { serializationDate?: number };
      const serializationDate = parsed.serializationDate;
      if (typeof serializationDate !== "number") continue;

      newestSerializationDate = Math.max(
        newestSerializationDate ?? serializationDate,
        serializationDate,
      );
      oldestSerializationDate = Math.min(
        oldestSerializationDate ?? serializationDate,
        serializationDate,
      );
    } catch {
      // Ignore malformed SDK cache entries; byte counts are still useful.
    }
  }

  return {
    entries,
    largestEntryBytes,
    newestSerializationDate: formatSerializationDate(newestSerializationDate),
    oldestSerializationDate: formatSerializationDate(oldestSerializationDate),
    totalBytes,
  };
}

function logSealdCacheDiagnostics(trackEvent?: TrackEventFn): void {
  if (!isWeb() || !trackEvent) return;

  try {
    trackEvent({
      ...getSealdCacheDiagnostics(window.localStorage),
      name: TRACK_EVENTS.SEALD_CACHE_DIAGNOSTICS,
      system_event: true,
    });
  } catch (error) {
    console.error(
      "[seald-cache] failed to read localStorage diagnostics",
      error,
    );
  }
}

/**
 * Removes localStorage entries that were written by the old localStorage cache
 * and have since expired. Called at SDK init to reclaim quota for users who
 * had entries accumulate before the migration to IndexedDB.
 *
 * Returns the number of entries removed (for telemetry / testing).
 */
export function clearExpiredSealdCacheEntries(
  storage: Pick<Storage, "getItem" | "removeItem" | "key" | "length">,
  ttlMs: number,
): number {
  const now = Date.now();
  const toDelete: string[] = [];

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith(SEALD_CACHE_PREFIX)) continue;

    const raw = storage.getItem(key);
    if (raw == null) {
      toDelete.push(key);
      continue;
    }

    try {
      const { serializationDate } = JSON.parse(raw) as {
        serializationDate?: number;
      };
      if (
        typeof serializationDate !== "number" ||
        now - serializationDate > ttlMs
      ) {
        toDelete.push(key);
      }
    } catch {
      toDelete.push(key);
    }
  }

  for (const key of toDelete) {
    storage.removeItem(key);
  }

  return toDelete.length;
}

async function createEncryptionSessionCacheWithFallback(
  args: Parameters<
    NonNullable<SDKArguments["createEncryptionSessionCache"]>
  >[0],
): Promise<EncryptionSessionCache> {
  if (!createLocalStorageEncryptionSessionCache) {
    throw new Error("Seald localStorage encryption session cache unavailable");
  }

  if (typeof indexedDB === "undefined") {
    return createLocalStorageEncryptionSessionCache(args);
  }

  try {
    return await createIndexedDBEncryptionSessionCache(args);
  } catch (error) {
    console.warn(
      "[seald-idb] IndexedDB encryption session cache unavailable; falling back to localStorage",
      error,
    );
    return createLocalStorageEncryptionSessionCache(args);
  }
}

/**
 * Creates the SDK arguments.
 *
 * On web, LocalStorage encryption session cache requires a persistent DB. We
 * only add cache + `databasePath` when `sessionId` is set (same shape as
 * `instantiateSealdSDK`).
 */
export function buildSealdSDKArguments({
  envContext,
  sessionId,
}: {
  envContext: EnvContext;
  sessionId?: string;
}): SDKArguments {
  const usePersistentDb = isWeb() && Boolean(sessionId);

  // Prefer IndexedDB (no quota limit) over localStorage. Fall back to the
  // SDK's built-in localStorage factory when IDB is unavailable or when
  // indexedDB.open fails at runtime (corrupt DB, disk full, browser bugs).
  const createEncryptionSessionCache = usePersistentDb
    ? createEncryptionSessionCacheWithFallback
    : undefined;

  const cacheAndDb: Partial<SDKArguments> = usePersistentDb
    ? {
        createEncryptionSessionCache,
        databasePath: `seald-session-${sessionId}`,
        encryptionSessionCacheTTL: SEALD_CACHE_TTL_MS,
      }
    : {};

  return {
    apiURL: envContext.config?.seald.apiUrl ?? "",
    appId: envContext.config?.seald.appId ?? "",
    plugins: [
      SealdSDKPluginSSKS2MR(envContext.config?.seald.ssksUrl),
      SealdSDKPluginSSKSPassword(envContext.config?.seald.ssksUrl),
    ],
    ...cacheAndDb,
    shouldWaterfallApi: false,
  };
}

/**
 * Instantiates and initializes the Seald SDK.
 */
export async function instantiateSealdSDK({
  accountId,
  databaseKey,
  envContext,
  sessionId,
  trackEvent,
}: {
  accountId: number | undefined;
  databaseKey: string | undefined;
  envContext: EnvContext;
  sessionId: string | undefined;
  trackEvent?: TrackEventFn;
}) {
  if (!SealdSDK) return;

  logSealdCacheDiagnostics(trackEvent);

  // Free up localStorage!
  if (isWeb()) {
    try {
      const removedCount = clearExpiredSealdCacheEntries(
        window.localStorage,
        SEALD_CACHE_TTL_MS,
      );
      if (removedCount > 0) {
        console.log(
          `[seald-migration] Evicted ${removedCount} legacy localStorage cache entries.`,
        );
      }
    } catch (e) {
      console.error(
        "[seald-migration] Failed to clear legacy storage entries",
        e,
      );
    }
  }

  const sdkArguments: SDKArguments = {
    ...buildSealdSDKArguments({ envContext, sessionId }),
    databaseKey: databaseKey ?? undefined,
  };

  sealdSDKInstance = SealdSDK(sdkArguments);
  sealdSDKInstance.setLogLevel("warn");
  try {
    await sealdSDKInstance.initialize();
  } catch (err) {
    console.error(`Seald db reset on init [account_id:${accountId}]`, err);
    try {
      await sealdSDKInstance.dropDatabase();
      // dropError won't be thrown in sdk>0.27, delete this trycatch and rune2e
    } catch (dropErr) {
      const dropErrMessage =
        dropErr instanceof Error ? dropErr.message : JSON.stringify(dropErr);
      if (!dropErrMessage.includes("instance is not initialized")) {
        throw dropErr;
      }
    }
    sealdSDKInstance = SealdSDK(sdkArguments);
    sealdSDKInstance.setLogLevel("warn");
    await sealdSDKInstance.initialize();
  }
}

/**
 * Helper to check that the SDK instance is registered.
 */
export const isSealdSDKRegistered = async (): Promise<boolean> => {
  const status = await sealdSDKInstance?.registrationStatus();
  return status === "registered";
};

export const getSealdSDKInstance = async (): Promise<SealdSDKType> => {
  if (!sealdSDKInstance) {
    throw new Error(`Seald SDK instance error: no instance available.`);
  }

  try {
    const isRegistered = await isSealdSDKRegistered();
    if (!isRegistered) {
      throw new Error(`Seald SDK instance status not "registered"`);
    }

    return sealdSDKInstance;
  } catch (err) {
    const error = getError(err);
    throw new Error(`get Seald SDK instance error: ${error.message}`, error);
  }
};
